import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from 'app/shared/api.service';
import { MqttService } from 'app/core/mqtt/mqtt.service';

@Component({
  selector: 'app-user-monitor',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './user-monitor.component.html',
  styleUrl: './user-monitor.component.scss'
})
export class UserMonitorComponent implements OnInit, OnDestroy {

  // ── Route params ──
  tenantCode: string = '';
  roomId: string = '';

  // ── API data ──
  displayData: any = null;
  isLoading: boolean = true;
  isTenant: boolean = false;

  // ── Clock ──
  currentTime: Date = new Date();
  private timerId: any;
  private pollTimer: any;
  private mqttSub?: Subscription;

  // ── Welcome override state (untuk MQTT trigger) ──
  private modeOverride: string | null = null;
  private welcomeTimer: any;
  private readonly WELCOME_DURATION_MS = 10_000;

  // ── Convenience getters ──
  get room() { return this.displayData?.room; }
  get property() { return this.displayData?.property; }
  get occupant() { return this.displayData?.occupant; }
  get facilities(): any[] { return this.displayData?.facilities || []; }
  get display() { return this.displayData?.display; }
  get weather() { return this.displayData?.weather; }
  get serverTime() { return this.displayData?.server_time; }

  get status(): string { return this.room?.status?.toLowerCase() || 'available'; }
  get isOccupied(): boolean { return this.status === 'occupied'; }

  /** 
   * Menyesuaikan mode dari API dengan state di HTML asli.
   * HTML asli punya mode: 'idle', 'facility', 'welcome', 'goodnight', 'goodmorning'
   */
  get mode(): string { 
    if (this.modeOverride) return this.modeOverride;
    
    const apiMode = this.display?.mode || 'idle';

    if (this.isOccupied) {
      if (apiMode === 'welcome') return 'welcome';
      
      // Jika kamar occupied tapi API memberikan 'idle', kita petakan ke goodmorning/goodnight
      // berdasarkan greeting key supaya background image sesuai.
      const key = this.display?.greeting?.key || 'morning';
      if (key === 'night') return 'goodnight';
      return 'goodmorning'; // Default untuk pagi/siang/sore (SelamatPagi.jpg)
    }
    
    return apiMode; 
  }

  get greeting() { return this.display?.greeting?.label || 'Selamat Datang'; }

  get formattedName() {
    const name: string = this.occupant?.full_name || '';
    if (!name) return { firstPart: '', lastPart: '' };
    const parts = name.trim().split(' ');
    if (parts.length <= 1) return { firstPart: '', lastPart: name };
    const lastPart = parts.pop();
    const firstPart = parts.join(' ');
    return { firstPart, lastPart };
  }

  constructor(
    private route: ActivatedRoute,
    private api: ApiService,
    private mqttService: MqttService
  ) {}

  ngOnInit(): void {
    // Clock ticker
    this.timerId = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);

    // Read route params: /monitor/:tenantCode/:roomId
    this.route.params.subscribe(params => {
      this.tenantCode = params['tenantCode'] || 'BNB';
      this.roomId = params['roomId'] || '';
      this.loadDisplayData();
    });

    // Poll every 60 seconds for fresh server data
    this.pollTimer = setInterval(() => {
      if (this.tenantCode && this.roomId) {
        this.loadDisplayData(false);
      }
    }, 60_000);

    // MQTT: connect first, subscribe will happen after API data is loaded
    this.mqttService.connect();
  }

  ngOnDestroy(): void {
    clearInterval(this.timerId);
    clearInterval(this.pollTimer);
    clearTimeout(this.welcomeTimer);
    this.mqttSub?.unsubscribe();
  }

  /** Fetch display data from public endpoint */
  async loadDisplayData(showLoader = true): Promise<void> {
    if (showLoader) this.isLoading = true;
    try {
      const endpoint = `api/v1/public/room-display/${this.tenantCode}/${this.roomId}`;
      const res = await this.api.getPublic(endpoint); // X-API-KEY header
      if (res?.success && res?.data) {
        this.displayData = res.data;
        this.isTenant = res.is_tenant ?? false;

        // Ambil topic MQTT dari API response
        const topic = this.displayData.mqtt?.topic;
        if (topic) {
          this.subscribeToMqtt(topic);
        }
      }
    } catch (e) {
      console.error('[Monitor] Failed to load display data', e);
    } finally {
      if (showLoader) this.isLoading = false;
    }
  }

  private currentMqttTopic: string | null = null;

  /** Subscribe to MQTT topic for this doorlock based on API response */
  private subscribeToMqtt(topic: string): void {
    if (this.currentMqttTopic === topic) return; // Hindari subscribe berkali-kali ke topik yang sama
    
    this.currentMqttTopic = topic;
    this.mqttService.subscribe(topic);

    // Unsubscribe listener sebelumnya (jika ada) supaya tidak ada duplikasi event
    if (this.mqttSub) {
      this.mqttSub.unsubscribe();
    }

    this.mqttSub = this.mqttService.messages$.subscribe(({ topic: t, message }) => {
      if (t === topic) {
        this.onCardTapped(message);
      }
    });
  }

  /** Called when MQTT trigger arrives (user tapped card on door) */
  private onCardTapped(payload: string): void {
    console.log('[Monitor] Card tapped, payload:', payload);
    // Immediately reload data from API
    this.loadDisplayData(false).then(() => {
      // Force mode to 'welcome' temporarily
      this.showWelcomeTemporarily();
    });
  }

  /** Force mode to 'welcome' for WELCOME_DURATION_MS then revert to API data */
  private showWelcomeTemporarily(): void {
    clearTimeout(this.welcomeTimer);
    this.modeOverride = 'welcome';
    this.welcomeTimer = setTimeout(() => {
      this.modeOverride = null;
    }, this.WELCOME_DURATION_MS);
  }
}
